<template>
    <tr :class="data.errors.length > 0 ? 'row-error' : ''">

        <td v-if="selectMode==='multiple'" class="align-self-center">
            <v-checkbox :multiple="false"
                        :value="data.id"
                        hide-details
                        @change="projectSelected"
                        v-model="selectedModel"
            ></v-checkbox>
        </td>

        <td v-if="columnVisible('owner')">
            <avatar :image-url="data.created_by.photo_url"
                    :person-name="data.created_by.name">
            </avatar>
        </td>

        <td class="truncate">

            <v-chip label
                    small
                    class="mr-1 pa-2 grey--text text--lighten-2"
                    color="grey darken-2">
                {{ data.id }}
            </v-chip>

            <priority-indicator class="ml-2"
                                :value="data.sampling_priority"
            ></priority-indicator>

            <!-- ERROR INDICATOR -->
            <span class="pl-2 cursor-pointer" v-if="data.errors.length > 0">
                <v-tooltip top color="primary" open-delay="250" max-width="500">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" icon small light color="#CA3939" @click="errorsDialogModel=true">
                            <v-icon class="glow mr-2">mdi-alert-circle</v-icon>
                        </v-btn>
                    </template>
                    Project contain errors. Click here to review them.
                </v-tooltip>
            </span>

            <!-- PROJECT NAME -->
            <span @click="editProject(data)" class="cursor-pointer">

                <v-tooltip top color="primary" open-delay="250" max-width="500">
                    <template v-slot:activator="{ on }">

                        <span v-on="on">
                            {{ data.name }}
                        </span>

                    </template>
                    <div class="pa-2">
                        <div class="body-2 font-weight-bold">
                            {{ data.name }}
                        </div>
                        <div class="mt-1 mb-1">
                            ID: {{ data.id }}
                        </div>
                        <div class="mt-1 mb-1">
                            Created at: {{ this.toLocalDateTime(data.created_at) }}
                        </div>
                        <template v-if="data.categories.length > 0">

                            <span class="body-2">Categories:</span>
                            <br>
                            <span>
                                <template v-for="(category) in data.categories">
                                    <v-chip small color="white" text-color="grey darken-2" label class="mr-1 mt-1">
                                        {{ category.name }}
                                    </v-chip>
                                </template>
                            </span>
                        </template>
                    </div>
                </v-tooltip>
            </span>

        </td>

        <!-- PROGRESS -->
        <td v-if="columnVisible('progress_percent')" class="cursor-help">
            <progress-column :project="data"></progress-column>
        </td>

        <!-- STATUS INDICATOR -->
        <td v-if="columnVisible('status')">
            <status-column :project="data"></status-column>
        </td>

        <!-- DASHBOARD -->
        <td v-if="columnVisible('dashboard')">

            <v-tooltip top color="primary" open-delay="250" v-if="hasPermission('view projects')">
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on"
                           fab
                           class="elevation-0 ml-4"
                           x-small
                           color="primary"
                           @click.stop="openProjectStats(data)">
                        <v-icon small>mdi-poll</v-icon>
                    </v-btn>
                </template>
                <span>Click to open dashboard</span>
            </v-tooltip>
        </td>

        <!-- RESPONSE RATE -->
        <td v-if="columnVisible('response rate')" class="cursor-help">
            <response-rate-column :project="data"></response-rate-column>
        </td>

        <!-- INCIDENCE RATE -->
        <td v-if="columnVisible('incidence rate')" class="cursor-help">
            <incidence-rate-column :project="data"></incidence-rate-column>
        </td>

        <!-- PROJECT RATING -->
        <td v-if="columnVisible('rating')">
            <v-tooltip top open-delay="250" color="primary">
                <template v-slot:activator="{ on }">
                    <v-chip v-on="on" :color="calculateRatingColor(data.rating)">{{ data.rating }}</v-chip>
                </template>
                <span>The average project rating by panelists on scale 1 to 10</span>
            </v-tooltip>
        </td>

        <!-- CALENDAR -->

        <!-- Check if start date is in the future -->
        <td v-if="toHumanTill(data.start_date, true) && columnVisible('calendar')">
            <v-tooltip top open-delay="250" color="primary">
                <template v-slot:activator="{ on }">
                    <div class="deadline no-wrap" v-on="on">
                        Starts {{ toHumanTill(data.start_date) }}
                    </div>
                </template>

                <div class="body-2 ma-2">
                    <v-icon class="mr-2">mdi-clock</v-icon>
                    {{ toLocalDateTime(data.start_date) }}
                </div>
            </v-tooltip>
        </td>

        <!-- Check if start date has passed -->
        <td v-if="!toHumanTill(data.start_date, true) && columnVisible('calendar')">
            <v-tooltip open-delay="250" top color="primary">
                <template v-slot:activator="{ on }">
                    <div class="deadline text-no-wrap" v-on="on">
                        {{ toHumanTill(data.end_date) }}
                    </div>
                </template>

                <div class="body-2 ma-2">
                    <v-icon class="mr-2">mdi-clock</v-icon>
                    {{ toLocalDateTime(data.end_date) }}
                </div>
            </v-tooltip>
        </td>

        <!-- ACTION BUTTONS -->
        <td class="px-0" v-if="columnVisible('actions')">
            <div class="no-wrap">

                <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            icon
                            class="ma-0"
                            text
                            v-on="on"
                            color="secondary">
                            <v-icon light color="white">mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>

                    <v-list class="pa-0">

                        <!-- VIEW PARTICIPANTS -->
                        <v-list-item
                            v-if="(data.invited_count > 0 || data.started_count > 0) && hasPermission('view projects')"
                            @click="viewParticipantsClicked(data)">
                            <v-list-item-avatar>
                                <v-icon>mdi-account-group</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-title>
                                Participants
                            </v-list-item-title>
                        </v-list-item>

                        <!-- DISQUALIFY BATCH -->
                        <v-list-item
                            v-if="data.completed_count > 0 && data.status!=='archived' && hasPermission('update projects')"
                            @click="disqualifyParticipantsClicked(data)">
                            <v-list-item-avatar>
                                <v-icon>mdi-head-remove-outline</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-title>
                                Batch disqualify
                            </v-list-item-title>
                        </v-list-item>

                        <!-- CLONE -->
                        <v-list-item v-if="hasPermission('create projects')"
                                     @click="cloneClicked(data.id)">
                            <v-list-item-avatar>
                                <v-icon>mdi-content-duplicate</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-title>
                                Clone project
                            </v-list-item-title>
                        </v-list-item>

                        <!-- DELETE -->
                        <v-list-item
                            v-if="data.invited_count===0 && data.status!=='online' && hasPermission('delete projects')"
                            @click="deleteClicked(data.id)">
                            <v-list-item-avatar>
                                <v-icon>mdi-trash-can-outline</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-title>
                                Delete project
                            </v-list-item-title>
                        </v-list-item>

                    </v-list>
                </v-menu>
            </div>
        </td>

        <project-errors-dialog v-model="errorsDialogModel"
                               :project-id="data.id"
                               :errors="data.errors">
        </project-errors-dialog>

    </tr>

</template>

<script>

import Avatar from '../../../components/UI/Avatar';
import PriorityIndicator from "../../../components/UI/PriorityIndicator";
import ProjectErrorsDialog from "./ProjectErrorsDialog";
import IncidenceRateColumn from "./IncidenceRateColumn";
import ResponseRateColumn from "./ResponseRateColumn";
import ProgressColumn from "./ProgressColumn";
import StatusColumn from "./StatusColumn";

import {mapState} from 'vuex';
import {progress} from '../../../components/mixins/progressMixin';
import {numberDisplay} from '../../../components/mixins/numberDisplayMixin';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
import {surveyRatingColor} from "../../../components/mixins/surveyRatingColorMixin";

export default {

    mixins: [progress, timeDisplay, surveyRatingColor, numberDisplay],

    components: {
        StatusColumn,
        ProgressColumn,
        ResponseRateColumn,
        IncidenceRateColumn,
        Avatar,
        PriorityIndicator,
        ProjectErrorsDialog
    },

    props: {
        data: {
            required: true,
            type: Object
        },
        columns: {
            required: true,
            type: Array
        },
        selectMode: {
            type: String,
            required: false,
            default: 'single',
            validator: (value) => {
                return ['single', 'multiple'].includes(value);
            }
        },
        selected: {
            type: Number | null,
            required: false,
            default: false,
        }
    },

    watch: {
        selected() {
            this.selectedModel = this.selected;
        }
    },

    data() {
        return {
            errorsDialogModel: false,
            selectedModel: null
        }
    },

    computed: {
        ...mapState('PanelSettings', [
            'settings'
        ]),
    },

    methods: {

        columnVisible(name) {
            return this.columns.find((column) => column.name === name);
        },
        editProject(project) {
            this.$emit('edit', project);
        },
        deleteClicked(projectId) {
            this.$emit('delete', projectId);
        },
        cloneClicked(projectId) {
            this.$emit('clone', projectId);
        },
        openProjectStats(project) {
            this.$emit('stats', project);
        },
        viewParticipantsClicked(project) {
            this.$emit('participants', project);
        },
        disqualifyParticipantsClicked(project) {
            this.$emit('disqualify', project);
        },
        projectSelected(value) {
            this.$emit(value ? 'select' : 'deselect', this.data.id);
        }
    },

    mounted() {
        this.selectedModel = this.selected;
    }
}
</script>

<style scoped>

.truncate {
    max-width: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.glow {
    border-radius: 50%;
    background-color: #fff;
    box-shadow: 0 0 5px 2px #ccc;
}

.row-error {
    background-color: rgba(255, 0, 0, 0.1);
}


</style>
